import { createAsyncThunk, createSlice, Draft, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import { message, Modal } from "antd";
import { MomentInput } from "moment/moment";
import { PageableSearchResult } from "../../lib/interfaces/PageableSearchResult";
import { LoadingStatusCode } from "../../lib/types/LoadingStatusCode";
import LoadingStatusCodes from "../../lib/const/loadingStatusCodes";
import {
  closeAddressVersionsRoute,
  closeEmailVersionsRoute,
  closePhoneVersionsRoute,
  createAccountVersionRoute,
  createContactPhoneRoute,
  createEmailRoute,
  createOrganizationContactIndividualRoute,
  deleteIndividualByIdRoute,
  fetchUsersRoute,
  getAccountInfoByIdRoute,
  fetchAccountsIndividualByNameRoute,
  getAccountsIndividualRoute,
  getAccountsOrganizationRoute,
  getAccountsPageableSearchRoute,
  getAccountsRoute,
  getAccountVersionsRoute,
  getAddressVersionsRoute,
  getEmailVersionsRoute,
  getIndividualInfoByIdRoute,
  getIndividualUpdateRoute,
  getIndividualVersionsRoute,
  getOrganizationVersionsRoute,
  getPhoneVersionsRoute,
  getSubscriberVersionsRoute,
  updateAddressVersionsRoute,
  updateEmailVersionsRoute,
  updatePhoneVersionsRoute,
} from "../../lib/routes/accountsTs";
import { Account } from "../../lib/interfaces/Account";
import { Individual } from "../../lib/interfaces/Individual";
import { User } from "../../lib/interfaces/User";
import { IindividualSearchValues } from "../../lib/interfaces/IindividualSearchValues";
import { AccountVersion } from "../../lib/interfaces/AccountVersion";
import { Organization } from "../../lib/interfaces/Organization";
import { Subscriber } from "../../lib/interfaces/Subscriber";
import { catchApiError } from "../../lib/utils/catchApiError";
import { AnyObject } from "../../lib/interfaces/AnyObject";
import { Email, Phone } from "../../lib/interfaces";
import { IAddress } from "../../lib/interfaces/Address";

type PageAccountDto = PageableSearchResult<Account[]>;
type PageIndividualDto = PageableSearchResult<Individual[]>;

type InitialState = {
  accountsDto?: PageAccountDto;
  accountsDtoLoading: LoadingStatusCode;
  accountListRowData: {
    accountVersions: Account[];
    accountVersionsLoading: LoadingStatusCode;
    organizationVersions: Organization[];
    organizationVersionsLoading: LoadingStatusCode;
    subscribersVersions: Subscriber[][];
    subscribersVersionsLoading: LoadingStatusCode;
  };
  accountsIndividual: PageIndividualDto[];
  accountsIndividualLoading: LoadingStatusCode;
  accountsIndividualSelect: Account[]; // для использования в фильтре расширенного поиска
  accountsIndividualSelectLoading: LoadingStatusCode;
  individualVersions: Individual[];
  individualVersionsLoading: LoadingStatusCode;
  responsibleUsers: User[];
  responsibleUsersTotalElements: number;
  responsibleUsersLoading: LoadingStatusCode;
  allUsers: User[];
  allUsersTotalElements: number;
  allUsersUsersLoading: LoadingStatusCode;
  getAccountByIdLoading: LoadingStatusCode;
  // TODO типизация accountByIdData;
  accountByIdData: AnyObject;
  createContactIndividualData: any[];
  createContactIndividualLoading: LoadingStatusCode;
  deleteIndividualData: any[];
  deleteIndividualLoading: LoadingStatusCode;
  getIndividualByIdLoading: LoadingStatusCode;
  getIndividualByIdData: AnyObject;
  updateContactIndividualLoading: LoadingStatusCode;
  getEmailVersionsData: Email[];
  getEmailVersionsLoading: LoadingStatusCode;
  updateEmailVersionsLoading: LoadingStatusCode;
  updatePhoneVersionsLoading: LoadingStatusCode;
  getPhoneVersionsData: Phone[];
  getPhoneVersionsLoading: LoadingStatusCode;
  getAddressVersionsLoading: LoadingStatusCode;
  getAddressVersionsData: IAddress[];
};
const initialState: InitialState = {
  accountsDtoLoading: LoadingStatusCodes.idle,
  accountListRowData: {
    accountVersions: [],
    accountVersionsLoading: LoadingStatusCodes.idle,
    organizationVersions: [],
    organizationVersionsLoading: LoadingStatusCodes.idle,
    subscribersVersions: [],
    subscribersVersionsLoading: LoadingStatusCodes.idle,
  },
  accountsIndividual: [],
  accountsIndividualLoading: LoadingStatusCodes.idle,
  individualVersions: [],
  individualVersionsLoading: LoadingStatusCodes.idle,
  responsibleUsers: [],
  responsibleUsersTotalElements: 0,
  responsibleUsersLoading: "idle",
  allUsers: [],
  allUsersTotalElements: 0,
  allUsersUsersLoading: "idle",
  getAccountByIdLoading: "idle",
  accountByIdData: {},
  createContactIndividualData: [],
  createContactIndividualLoading: LoadingStatusCodes.idle,
  deleteIndividualData: [],
  deleteIndividualLoading: LoadingStatusCodes.idle,
  getIndividualByIdLoading: LoadingStatusCodes.idle,
  getIndividualByIdData: {},
  updateContactIndividualLoading: LoadingStatusCodes.idle,
  getEmailVersionsData: [],
  getEmailVersionsLoading: LoadingStatusCodes.idle,
  updateEmailVersionsLoading: LoadingStatusCodes.idle,
  updatePhoneVersionsLoading: LoadingStatusCodes.idle,
  getPhoneVersionsLoading: LoadingStatusCodes.idle,
  getPhoneVersionsData: [],
  getAddressVersionsLoading: LoadingStatusCodes.idle,
  getAddressVersionsData: [],
  accountsIndividualSelect: [],
  accountsIndividualSelectLoading: "idle",
};

const getAccountsDto = createAsyncThunk(
  "accounts/getAccountDto",
  async ({ page, sort = "id" }: { page: number; sort?: string }) => {
    const response = axios.get(getAccountsRoute({ page, sort }));
    const { data }: { data: PageAccountDto } = await response;
    return data;
  }
);

const getAccountsOrganization = createAsyncThunk(
  "accounts/getAccountsOrganization",
  async ({ page }: { page: number }) => {
    try {
      const response = axios.get(getAccountsOrganizationRoute({ page }));
      const { data }: { data: PageAccountDto } = await response;
      return data;
    } catch (e) {
      const errorOptions = {
        title: "Ошибка загрузки юрлиц",
        content: "Что-то пошло не так... ",
      };
      if (e instanceof Error) {
        errorOptions.content = e.message;
      }
      Modal.error(errorOptions);
      throw new Error(errorOptions.content);
    }
  }
);

const getAccountsIndividual = createAsyncThunk(
  "accounts/getAccountsIndividual",
  async (params: IindividualSearchValues) => {
    try {
      const response = axios.get(getAccountsIndividualRoute(params));
      const { data }: { data: PageAccountDto } = await response;
      return data;
    } catch (e) {
      const errorOptions = {
        title: "Ошибка загрузки физлиц",
        content: "Что-то пошло не так... ",
      };
      if (e instanceof Error) {
        errorOptions.content = e.message;
      }
      Modal.error(errorOptions);
      throw new Error(errorOptions.content);
    }
  }
);

// Для целей поиска в фильтрах
export const fetchAccountsIndividualByName = createAsyncThunk(
  "accounts/fetchAccountsIndividualByName",
  async (fullName: string) => {
    try {
      const response = axios.get(fetchAccountsIndividualByNameRoute(fullName));
      const { data } = await response;
      return data;
    } catch (e) {
      const errorOptions = {
        title: "Ошибка загрузки физлиц",
        content: "Что-то пошло не так... ",
      };
      if (e instanceof Error) {
        errorOptions.content = e.message;
      }
      Modal.error(errorOptions);
      throw new Error(errorOptions.content);
    }
  }
);

const getAccountVersions = createAsyncThunk(
  "accounts/getAccountVersions",
  async ({ accountId }: { accountId: number }) => {
    const response = axios.get(getAccountVersionsRoute({ accountId }));
    const { data }: { data: AccountVersion } = await response;
    return data;
  }
);

const fetchIndividualVersions = createAsyncThunk(
  "accounts/fetchIndividualVersions",
  async ({ individualId }: { individualId: number }) => {
    try {
      const response = axios.get(getIndividualVersionsRoute({ individualId }));
      const { data }: { data: Individual } = await response;
      return data;
    } catch (e) {
      const errorOptions = {
        title: "Ошибка загрузки версий для физлиц",
        content: "Что-то пошло не так... ",
      };
      if (e instanceof Error) {
        errorOptions.content = e.message;
      }
      Modal.error(errorOptions);
      throw new Error(errorOptions.content);
    }
  }
);

const getOrganizationVersions = createAsyncThunk(
  "accounts/getOrganizationVersions",
  async ({ organizationId }: { organizationId: number }) => {
    const response = axios.get(getOrganizationVersionsRoute({ organizationId }));
    const { data }: { data: Organization } = await response;
    return data;
  }
);

const getSubscribersVersions = createAsyncThunk(
  "accounts/getSubscribersVersions",
  async ({ subscribersId }: { subscribersId: number[] }) => {
    const request = Promise.all(
      subscribersId.map((id) => axios.get(getSubscriberVersionsRoute({ organizationId: id })))
    );
    const res = await request;
    return res.map((item) => item.data);
  }
);

const createAccountVersion = createAsyncThunk(
  "accounts/createAccountVersion",
  async ({
    accountId,
    fromDate,
    payload,
  }: {
    accountId: number;
    fromDate: string;
    payload: {
      id: number;
      toDate: string;
      accountStatusId: number;
      organization?: Organization;
      individual?: Individual;
      subscribers: Subscriber[];
      accTypeId: number;
    };
  }) => {
    await axios.patch(createAccountVersionRoute({ accountId, fromDate }), payload);
  }
);

const accountsPageableSearch = createAsyncThunk(
  "accounts/accountsPageableSearch",
  async (params: {
    fullName?: string;
    shortName?: string;
    propertyTypeId?: string;
    propertyStringValue?: string;
    externalId?: string;
    agreementNumber?: string;
    agreementId?: string;
    accountStatusId?: string;
    billDeliveryEmail?: string;
    page: number;
    id?: string;
    phone?: string;
    accTypeId?: string;
  }) => {
    const response = axios.get(
      getAccountsPageableSearchRoute({
        ...params,
      })
    );
    const { data }: { data: PageAccountDto } = await response;
    return data;
  }
);

// Получение менеджеров B2O (responsibleUser) аккаунта
export const fetchResponsibleUsers = createAsyncThunk(
  "accounts/fetchResponsibleUsers",
  async (props: { userId: number[]; page?: number; size?: number }) => {
    try {
      const response = axios.get(fetchUsersRoute(props));
      const { data } = await response;
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки пользователей. Попробуйте позже",
      });
    }
  }
);

// Получение списка пользователей для выбора в ходе создания аккаунта
export const fetchAllUsers = createAsyncThunk(
  "accounts/fetchAllUsers",
  async (props: { page?: number; size?: number }) => {
    try {
      const response = axios.get(fetchUsersRoute(props));
      const { data } = await response;
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки пользователей. Попробуйте позже",
      });
    }
  }
);

export const getAccountById = createAsyncThunk(
  "accounts/getAccountById",
  async (data: { accountId: string; onDate?: string; simpleView?: boolean }) => {
    const { accountId, onDate, simpleView } = data;
    try {
      const response = axios.get(getAccountInfoByIdRoute(accountId, { onDate, simpleView }));
      const { data } = await response;
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки пользователей. Попробуйте позже",
      });
    }
  }
);

export const updateContactIndividual = createAsyncThunk(
  "accounts/updateContactIndividual",
  async (data: {
    individualId: number;
    fromDate: MomentInput;
    force: boolean;
    body: {
      firstName: string;
      middleName: string;
      lastName: string;
      sex: string;
    };
  }) => {
    const { individualId, fromDate, force, body } = data;
    message.loading("Обновление контактного лица", 0);
    try {
      const response = axios.patch(
        getIndividualUpdateRoute({ individualId, fromDate, force }),
        body
      );
      const { data } = await response;
      message.destroy();
      const msg = force ? "Версия успешно обновлена" : "Версия успешно создана";
      message.success(msg);
      return data;
    } catch (e) {
      message.destroy();
      const msg = force ? "Ошибка обновления версии" : "Ошибка создания версии";
      return catchApiError({
        e,
        title: msg,
      });
    }
  }
);

export const createContactIndividual = createAsyncThunk(
  "accounts/createContactIndividual",
  async (data: { organizationId: number | string; body: any }) => {
    const { organizationId, body } = data;
    message.loading("Создание контактного лица", 0);
    try {
      const response = axios.post(createOrganizationContactIndividualRoute(organizationId), body);
      const { data } = await response;
      message.destroy();
      message.success("Контактное лицо успешно добавлено");
      return data;
    } catch (e) {
      message.destroy();
      return catchApiError({
        e,
        title: "Ошибка добавления контактного лица",
      });
    }
  }
);

export const getIndividualInfoById = createAsyncThunk(
  "accounts/getIndividualInfoById",
  async (data: { individualId: string; onDate?: string }) => {
    const { individualId, onDate } = data;
    try {
      const response = axios.get(getIndividualInfoByIdRoute(individualId, { onDate }));
      const { data }: { data: PageAccountDto } = await response;
      return data;
    } catch (e) {
      return catchApiError({
        e,
        title: "Ошибка загрузки деталей. Попробуйте позже",
      });
    }
  }
);

export const deleteIndividual = createAsyncThunk(
  "accounts/deleteIndividual",
  async (data: { individualId: number; fromDate?: string }) => {
    const { individualId, fromDate } = data;
    try {
      const response = axios.delete(deleteIndividualByIdRoute(individualId, { fromDate }));
      const { data } = await response;
      message.success("Контактное лицо удалено");
      return data;
    } catch (e) {
      message.destroy();
      return catchApiError({
        e,
        title: "Произошла ошибка. Пожалуйста, повторите попытку позднее",
      });
    }
  }
);

const getEmailVersions = createAsyncThunk(
  "accounts/getEmailVersions",

  async (emailId: number) => {
    try {
      const response = axios.get(getEmailVersionsRoute(emailId));
      const { data } = await response;
      return data;
    } catch (e) {
      message.destroy();
      return catchApiError({
        e,
        title: "Ошибка загрузки email. Попробуйте позже",
      });
    }
  }
);

const createContactEmail = createAsyncThunk(
  "accounts/createContactEmail",
  async (data: { individualId: string; fromDate?: string; body: Email }) => {
    const { individualId, fromDate, body } = data;
    try {
      const response = axios.post(createEmailRoute(individualId, { fromDate }), body);
      const { data } = await response;
      message.success("Электронная почта успешно создана");
      return data;
    } catch (e) {
      message.destroy();
      return catchApiError({
        e,
        title: "Ошибка добавления электронной почты",
      });
    }
  }
);

const updateEmailVersions = createAsyncThunk(
  "accounts/updateEmailVersions",
  async (data: {
    emailId: number;
    fromDate: MomentInput;
    force: boolean;
    body: { email: string };
  }) => {
    const { emailId, fromDate, force, body } = data;
    try {
      const response = axios.patch(updateEmailVersionsRoute(emailId, { fromDate, force }), body);
      const { data } = await response;
      message.destroy();
      const msg = force ? "Версия успешно обновлена" : "Версия успешно создана";
      message.success(msg);
      return data;
    } catch (e) {
      message.destroy();
      return catchApiError({
        e,
        title: "Ошибка обновления версии",
      });
    }
  }
);

const closeEmailVersion = createAsyncThunk(
  "accounts/closeEmailVersion",
  async (data: { emailId: number; fromDate: string }) => {
    const { emailId, fromDate } = data;
    try {
      const response = axios.delete(closeEmailVersionsRoute(emailId, { fromDate }));
      const { data } = await response;
      message.success("Дата закрытия успешно изменена");
      return data;
    } catch (e) {
      message.destroy();
      return catchApiError({
        e,
        title: "Ошибка обновления версии",
      });
    }
  }
);

const getPhoneVersions = createAsyncThunk(
  "accounts/getPhoneVersions",

  async (phoneId: number) => {
    try {
      const response = axios.get(getPhoneVersionsRoute(phoneId));
      const { data } = await response;
      return data;
    } catch (e) {
      message.destroy();
      return catchApiError({
        e,
        title: "Ошибка загрузки телефона. Попробуйте позже",
      });
    }
  }
);

const createContactPhone = createAsyncThunk(
  "accounts/createContactPhone",
  async (data: { individualId: string; fromDate?: string; body: Phone }) => {
    const { individualId, fromDate, body } = data;
    try {
      const response = axios.post(createContactPhoneRoute(individualId, { fromDate }), body);
      const { data } = await response;
      message.success("Контактный телефон успешно создан");
      return data;
    } catch (e) {
      message.destroy();
      return catchApiError({
        e,
        title: "Ошибка добавления номера телефона",
      });
    }
  }
);

const updatePhoneVersions = createAsyncThunk(
  "accounts/updatePhoneVersions",
  async (data: {
    phoneId: number;
    fromDate: MomentInput;
    force: boolean;
    body: { phone: string; countryCode: string };
  }) => {
    const { phoneId, fromDate, force, body } = data;
    try {
      const response = axios.patch(updatePhoneVersionsRoute(phoneId, { fromDate, force }), body);
      const { data } = await response;
      message.destroy();
      const msg = force ? "Версия успешно обновлена" : "Версия успешно создана";
      message.success(msg);
      return data;
    } catch (e) {
      message.destroy();
      return catchApiError({
        e,
        title: "Ошибка обновления версии",
      });
    }
  }
);

const closePhoneVersion = createAsyncThunk(
  "accounts/closePhoneVersion",
  async (data: { phoneId: number; fromDate: string }) => {
    const { phoneId, fromDate } = data;
    try {
      const response = axios.delete(closePhoneVersionsRoute(phoneId, { fromDate }));
      const { data } = await response;
      message.success("Дата закрытия успешно изменена");
      return data;
    } catch (e) {
      message.destroy();
      return catchApiError({
        e,
        title: "Ошибка обновления версии",
      });
    }
  }
);

const getAddressVersions = createAsyncThunk(
  "accounts/getAddressVersions",

  async (addressId: number) => {
    try {
      const response = axios.get(getAddressVersionsRoute(addressId));
      const { data } = await response;
      return data;
    } catch (e) {
      message.destroy();
      return catchApiError({
        e,
        title: "Ошибка загрузки адреса. Попробуйте позже",
      });
    }
  }
);

const updateAddressVersions = createAsyncThunk(
  "accounts/updateAddressVersions",
  async (data: {
    addressId: number;
    fromDate: MomentInput;
    force: boolean;
    body: { countryId: number; regionId: number; unstructuredAddress: string; postCode: " " };
  }) => {
    const { addressId, fromDate, force, body } = data;
    try {
      const response = axios.patch(
        updateAddressVersionsRoute(addressId, { fromDate, force }),
        body
      );
      const { data } = await response;
      message.destroy();
      const msg = force ? "Версия успешно обновлена" : "Версия успешно создана";
      message.success(msg);
      return data;
    } catch (e) {
      message.destroy();
      return catchApiError({
        e,
        title: "Ошибка загрузки адреса. Попробуйте позже",
      });
    }
  }
);

const closeAddressVersion = createAsyncThunk(
  "accounts/closeAddressVersion",
  async (data: { addressId: number; fromDate: string }) => {
    const { addressId, fromDate } = data;
    try {
      const response = axios.delete(closeAddressVersionsRoute(addressId, { fromDate }));
      const { data } = await response;
      message.success("Дата закрытия успешно изменена");
      return data;
    } catch (e) {
      message.destroy();
      return catchApiError({
        e,
        title: "Ошибка обновления версии",
      });
    }
  }
);

export const accountsSlice = createSlice({
  name: "accounts",
  initialState,
  reducers: {
    cleanAccountsIndividualSelect: (state) => {
      state.accountsIndividualSelect = [];
      state.accountsIndividualSelectLoading = "idle";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAccountsDto.pending, (state) => {
        state.accountsDtoLoading = LoadingStatusCodes.pending;
      })
      .addCase(
        getAccountsDto.fulfilled,
        (state: Draft<any>, action: PayloadAction<PageAccountDto>) => {
          state.accountsDtoLoading = LoadingStatusCodes.fulfilled;
          state.accountsDto = action.payload;
        }
      )
      .addCase(getAccountsDto.rejected, (state) => {
        state.accountsDtoLoading = LoadingStatusCodes.rejected;
      })
      .addCase(getAccountsOrganization.pending, (state) => {
        state.accountsDtoLoading = LoadingStatusCodes.pending;
      })
      .addCase(
        getAccountsOrganization.fulfilled,
        (state: Draft<any>, action: PayloadAction<PageAccountDto>) => {
          state.accountsDtoLoading = LoadingStatusCodes.fulfilled;
          state.accountsDto = action.payload;
        }
      )
      .addCase(getAccountsOrganization.rejected, (state) => {
        state.accountsDtoLoading = LoadingStatusCodes.rejected;
      })
      .addCase(getAccountsIndividual.pending, (state) => {
        state.accountsIndividualLoading = LoadingStatusCodes.pending;
      })
      .addCase(
        getAccountsIndividual.fulfilled,
        (state: Draft<any>, action: PayloadAction<PageAccountDto>) => {
          state.accountsIndividualLoading = LoadingStatusCodes.fulfilled;
          state.accountsIndividual = action.payload;
        }
      )
      .addCase(getAccountsIndividual.rejected, (state) => {
        state.accountsIndividualLoading = LoadingStatusCodes.rejected;
      })
      .addCase(fetchAccountsIndividualByName.pending, (state) => {
        state.accountsIndividualSelectLoading = "pending";
      })
      .addCase(fetchAccountsIndividualByName.fulfilled, (state, { payload }) => {
        state.accountsIndividualSelectLoading = "fulfilled";
        state.accountsIndividualSelect = payload.content;
      })
      .addCase(fetchAccountsIndividualByName.rejected, (state) => {
        state.accountsIndividualSelectLoading = "rejected";
      })
      .addCase(fetchIndividualVersions.pending, (state) => {
        state.individualVersionsLoading = LoadingStatusCodes.pending;
      })
      .addCase(fetchIndividualVersions.rejected, (state) => {
        state.individualVersionsLoading = LoadingStatusCodes.rejected;
      })
      .addCase(
        fetchIndividualVersions.fulfilled,
        (state: Draft<any>, action: PayloadAction<Individual>) => {
          state.individualVersionsLoading = LoadingStatusCodes.fulfilled;
          state.individualVersions = action.payload;
        }
      )
      .addCase(getAccountVersions.pending, (state) => {
        state.accountListRowData.accountVersionsLoading = LoadingStatusCodes.pending;
      })
      .addCase(
        getAccountVersions.fulfilled,
        (state: Draft<any>, action: PayloadAction<AccountVersion>) => {
          state.accountListRowData.accountVersionsLoading = LoadingStatusCodes.fulfilled;
          state.accountListRowData.accountVersions = action.payload;
        }
      )
      .addCase(getAccountVersions.rejected, (state) => {
        state.accountListRowData.accountVersionsLoading = LoadingStatusCodes.rejected;
      })
      .addCase(getOrganizationVersions.pending, (state) => {
        state.accountListRowData.organizationVersionsLoading = LoadingStatusCodes.pending;
      })
      .addCase(
        getOrganizationVersions.fulfilled,
        (state: Draft<any>, action: PayloadAction<Organization>) => {
          state.accountListRowData.organizationVersionsLoading = LoadingStatusCodes.fulfilled;
          state.accountListRowData.organizationVersions = action.payload;
        }
      )
      .addCase(getOrganizationVersions.rejected, (state) => {
        state.accountListRowData.organizationVersionsLoading = LoadingStatusCodes.rejected;
      })
      .addCase(getSubscribersVersions.pending, (state) => {
        state.accountListRowData.subscribersVersionsLoading = LoadingStatusCodes.pending;
      })
      .addCase(
        getSubscribersVersions.fulfilled,
        (state: Draft<any>, action: PayloadAction<any>) => {
          state.accountListRowData.subscribersVersionsLoading = LoadingStatusCodes.fulfilled;
          state.accountListRowData.subscribersVersions = action.payload;
        }
      )
      .addCase(getSubscribersVersions.rejected, (state) => {
        state.accountListRowData.subscribersVersionsLoading = LoadingStatusCodes.rejected;
      })
      .addCase(accountsPageableSearch.pending, (state) => {
        state.accountsDtoLoading = LoadingStatusCodes.pending;
      })
      .addCase(
        accountsPageableSearch.fulfilled,
        (state: Draft<any>, action: PayloadAction<any>) => {
          state.accountsDtoLoading = LoadingStatusCodes.fulfilled;
          state.accountsDto = action.payload;
        }
      )
      .addCase(accountsPageableSearch.rejected, (state) => {
        state.accountsDtoLoading = LoadingStatusCodes.rejected;
      })
      .addCase(fetchResponsibleUsers.pending, (state) => {
        state.responsibleUsersLoading = "pending";
      })
      .addCase(fetchResponsibleUsers.fulfilled, (state, { payload }) => {
        state.responsibleUsersLoading = "fulfilled";
        state.responsibleUsersTotalElements = payload.totalElements;
        state.responsibleUsers = payload.content;
      })
      .addCase(fetchResponsibleUsers.rejected, (state) => {
        state.responsibleUsersLoading = "rejected";
      })
      .addCase(fetchAllUsers.pending, (state) => {
        state.allUsersUsersLoading = "pending";
      })
      .addCase(fetchAllUsers.fulfilled, (state, { payload }) => {
        state.allUsersUsersLoading = "fulfilled";
        state.allUsersTotalElements = payload.totalElements;
        state.allUsers = payload.content;
      })
      .addCase(fetchAllUsers.rejected, (state) => {
        state.allUsersUsersLoading = "rejected";
      })
      .addCase(getAccountById.pending, (state) => {
        state.getAccountByIdLoading = LoadingStatusCodes.pending;
      })
      .addCase(getAccountById.fulfilled, (state, { payload }) => {
        state.getAccountByIdLoading = LoadingStatusCodes.fulfilled;
        state.accountByIdData = payload;
      })
      .addCase(getAccountById.rejected, (state) => {
        state.getAccountByIdLoading = LoadingStatusCodes.rejected;
      })
      .addCase(getIndividualInfoById.pending, (state) => {
        state.getIndividualByIdLoading = LoadingStatusCodes.pending;
      })
      .addCase(getIndividualInfoById.fulfilled, (state, { payload }) => {
        state.getIndividualByIdLoading = LoadingStatusCodes.fulfilled;
        state.getIndividualByIdData = payload;
      })
      .addCase(getIndividualInfoById.rejected, (state) => {
        state.getIndividualByIdLoading = LoadingStatusCodes.rejected;
      })
      .addCase(createContactIndividual.pending, (state) => {
        state.createContactIndividualLoading = LoadingStatusCodes.pending;
      })
      .addCase(createContactIndividual.fulfilled, (state, { payload }) => {
        state.createContactIndividualLoading = LoadingStatusCodes.fulfilled;
        state.createContactIndividualData = payload;
      })
      .addCase(createContactIndividual.rejected, (state) => {
        state.createContactIndividualLoading = LoadingStatusCodes.rejected;
      })
      .addCase(deleteIndividual.pending, (state) => {
        state.deleteIndividualLoading = LoadingStatusCodes.pending;
      })
      .addCase(deleteIndividual.fulfilled, (state, { payload }) => {
        state.deleteIndividualLoading = LoadingStatusCodes.fulfilled;
        state.deleteIndividualData = payload;
      })
      .addCase(deleteIndividual.rejected, (state) => {
        state.deleteIndividualLoading = LoadingStatusCodes.rejected;
      })
      .addCase(updateContactIndividual.pending, (state) => {
        state.updateContactIndividualLoading = LoadingStatusCodes.pending;
      })
      .addCase(updateContactIndividual.fulfilled, (state) => {
        state.updateContactIndividualLoading = LoadingStatusCodes.fulfilled;
      })
      .addCase(updateContactIndividual.rejected, (state) => {
        state.updateContactIndividualLoading = LoadingStatusCodes.rejected;
      })
      .addCase(getEmailVersions.pending, (state) => {
        state.getEmailVersionsLoading = LoadingStatusCodes.pending;
      })
      .addCase(getEmailVersions.fulfilled, (state, { payload }) => {
        state.getEmailVersionsLoading = LoadingStatusCodes.fulfilled;
        state.getEmailVersionsData = [...payload].sort(
          (a, b) => Date.parse(a.toDate) - Date.parse(b.toDate)
        );
      })
      .addCase(getEmailVersions.rejected, (state) => {
        state.getEmailVersionsLoading = LoadingStatusCodes.rejected;
      })
      .addCase(getPhoneVersions.pending, (state) => {
        state.getPhoneVersionsLoading = LoadingStatusCodes.pending;
      })
      .addCase(getPhoneVersions.fulfilled, (state, { payload }) => {
        state.getPhoneVersionsLoading = LoadingStatusCodes.fulfilled;
        state.getPhoneVersionsData = [...payload].sort(
          (a, b) => Date.parse(a.toDate) - Date.parse(b.toDate)
        );
      })
      .addCase(getPhoneVersions.rejected, (state) => {
        state.getPhoneVersionsLoading = LoadingStatusCodes.rejected;
      })

      .addCase(getAddressVersions.pending, (state) => {
        state.getAddressVersionsLoading = LoadingStatusCodes.pending;
      })
      .addCase(getAddressVersions.fulfilled, (state, { payload }) => {
        state.getAddressVersionsLoading = LoadingStatusCodes.fulfilled;
        state.getAddressVersionsData = payload;
      })
      .addCase(getAddressVersions.rejected, (state) => {
        state.getAddressVersionsLoading = LoadingStatusCodes.rejected;
      })

      .addCase(updateEmailVersions.pending, (state) => {
        state.updateEmailVersionsLoading = LoadingStatusCodes.pending;
      })
      .addCase(updateEmailVersions.fulfilled, (state) => {
        state.updateEmailVersionsLoading = LoadingStatusCodes.fulfilled;
      })
      .addCase(updateEmailVersions.rejected, (state) => {
        state.updateEmailVersionsLoading = LoadingStatusCodes.rejected;
      })
      .addCase(updatePhoneVersions.pending, (state) => {
        state.updatePhoneVersionsLoading = LoadingStatusCodes.pending;
      })
      .addCase(updatePhoneVersions.fulfilled, (state) => {
        state.updatePhoneVersionsLoading = LoadingStatusCodes.fulfilled;
      })
      .addCase(updatePhoneVersions.rejected, (state) => {
        state.updatePhoneVersionsLoading = LoadingStatusCodes.rejected;
      });
  },
});

const { reducer, actions: accountTsActions } = accountsSlice;

export {
  reducer,
  accountTsActions,
  getAccountsDto,
  getAccountVersions,
  getOrganizationVersions,
  getSubscribersVersions,
  createAccountVersion,
  accountsPageableSearch,
  getAccountsOrganization,
  getAccountsIndividual,
  fetchIndividualVersions,
  getEmailVersions,
  updateEmailVersions,
  closeEmailVersion,
  getPhoneVersions,
  updatePhoneVersions,
  closePhoneVersion,
  getAddressVersions,
  updateAddressVersions,
  closeAddressVersion,
  createContactPhone,
  createContactEmail,
};
